/**
 * Progress model — what gets saved per student. Pure functions; storage lives in src/data.
 */
import { SETTINGS, UNITS, stagesFor, unitById, factKey, parseKey, keyOp, type Op } from "./curriculum";
export { factKey };

/** 0 not tried · 1 revisit (missed) · 2 correct · 3 fluent (no tools, under 3 s). */
export type FactStatus = 0 | 1 | 2 | 3;

export interface FactRecord {
  s: FactStatus;
  /** Best time in ms when fluent, for the teacher view. */
  ms?: number;
}

export interface UnitRecord {
  /** Highest unlocked stage index. stage === stages.length means the unit is complete. */
  stage: number;
  /** ISO date (YYYY-MM-DD, local) the warm-up was last completed. */
  warm: string;
}

export interface StudentProgress {
  facts: Record<string, FactRecord>;
  units: Record<string, UnitRecord>;
  currentUnit: string;
  /** Count of distinct days the warm-up was completed — the streak's raw material. */
  warmDays: string[];
  /** The lesson engine (Level 3 lessons and up). Optional so older saved progress still loads. */
  lessons?: Record<string, LessonRecord>;
  skills?: Record<string, SkillRecord>;
  /** Check-ups (pre / post): item id → right (true), not right (false), skipped (null). */
  checks?: Record<string, { results: Record<string, boolean | null>; date?: string }>;
  /** A teacher's floor: "everyone through this lesson by this date." The student may go past it. */
  floor?: { lesson: string; due?: string };
  /** Where the diagnostic placed the student (a lesson id, any level). Lessons before it are open but not required. */
  placement?: { lesson: string; by: "teacher" | "diagnostic"; date?: string };
  /** The last placement diagnostic: the facts sample, each lesson probed (right of 2), and where it placed. */
  diagnostic?: { date: string; facts: { right: number; n: number; medianMs: number }; probes: Record<string, number>; placed: string; solid: boolean; divSolid?: boolean };
  /** The facts check (research pre/post): the beginning sitting and the end sitting. */
  factsTest?: { pre?: FactsResult; post?: FactsResult };
}
/** One sitting of the facts check: form, local date, and per item right / wrong / skipped (null) with ms. */
export interface FactsResult { form: "A" | "B"; date: string; items: Record<string, { c: boolean | null; ms: number }> }

export interface LessonRecord {
  /** Step ids finished. */
  done: string[];
  /** The step the student was on. */
  at?: string;
  /** "Show me you know this": the date tried, and the steps it skipped. */
  checkOut?: { date: string; passed: string[] };
}

export interface SkillRecord {
  /** Problems finished, and how many were right on the first try with no hints. */
  n: number;
  first: number;
  /** Local date the skill was first mastered. */
  mastered?: string;
  /** Practice blocks that ended at the cap without mastery — the teacher is told. */
  stuck?: number;
}

export const emptyProgress = (): StudentProgress => ({ facts: {}, units: {}, currentUnit: UNITS[0]!.id, warmDays: [], lessons: {}, skills: {} });

export const lessonRecord = (p: StudentProgress, id: string): LessonRecord => ((p.lessons ??= {})[id] ??= { done: [] });
export const skillRecord = (p: StudentProgress, id: string): SkillRecord => ((p.skills ??= {})[id] ??= { n: 0, first: 0 });

/** A times-tables unit is finished when every stage is passed. */
export const unitDone = (p: StudentProgress, unitId: string): boolean => (p.units[unitId]?.stage ?? 0) >= stagesFor(unitById(unitId)).length;

export const unitRecord = (p: StudentProgress, id: string): UnitRecord => p.units[id] ?? (p.units[id] = { stage: 0, warm: "" });

export const localDate = (d = new Date()): string => d.toLocaleDateString("en-CA");

export const warmDoneToday = (p: StudentProgress, unitId: string, today = localDate()): boolean => unitRecord(p, unitId).warm === today;

export function markWarmDone(p: StudentProgress, unitId: string, today = localDate()): void {
  unitRecord(p, unitId).warm = today;
  if (!p.warmDays.includes(today)) p.warmDays.push(today);
}

/** Facts in these tables currently marked "revisit". */
export const revisitIn = (p: StudentProgress, tables: number[], op: Op = "mul"): string[] =>
  Object.keys(p.facts).filter((k) => p.facts[k]!.s === 1 && keyOp(k) === op && tables.includes(parseKey(k)[0]));

/** Times facts not yet fluent (tried and missed, or correct but slow/with tools), for the division lessons' review. */
export const strugglingIn = (p: StudentProgress, tables: number[]): string[] =>
  Object.keys(p.facts).filter((k) => p.facts[k]!.s > 0 && p.facts[k]!.s < 3 && keyOp(k) === "mul" && tables.includes(parseKey(k)[0]));

export interface Attempt {
  a: number;
  b: number;
  correct: boolean;
  /** First try on this problem in this worksheet. */
  firstTry: boolean;
  /** Stage is timed (Stage 4 / mixed review). */
  timed: boolean;
  /** Student used a tool (keypad/anchor) in Stage 3. */
  usedTool: boolean;
  ms: number;
  op?: Op;
}

/** Apply one answer to the fact record. Returns whether it counted as fluent. */
export function recordAttempt(p: StudentProgress, at: Attempt): boolean {
  const k = factKey(at.a, at.b, at.op ?? "mul");
  const fs = p.facts[k] ?? (p.facts[k] = { s: 0 });
  if (!at.correct) {
    fs.s = 1;
    return false;
  }
  if (!at.firstTry) {
    fs.s = 1; // missed once on this sheet: it comes back
    return false;
  }
  const fluent = at.timed && !at.usedTool && at.ms < SETTINGS.FLUENT_MS;
  if (fluent) {
    fs.s = 3;
    fs.ms = fs.ms === undefined ? at.ms : Math.min(fs.ms, at.ms);
  } else if (fs.s !== 3 || at.timed) {
    fs.s = 2;
  }
  return fluent;
}

/** Pass a stage: unlock the next one. Returns true if the unit is now complete. */
export function passStage(p: StudentProgress, unitId: string, stageIndex: number): boolean {
  const u = unitRecord(p, unitId);
  u.stage = Math.max(u.stage, stageIndex + 1);
  return u.stage >= stagesFor(unitById(unitId)).length;
}

/** The daily warm-up streak: consecutive days ending today or yesterday. */
export function streak(p: StudentProgress, today = new Date()): number {
  const days = new Set(p.warmDays);
  let n = 0;
  const d = new Date(today);
  if (!days.has(localDate(d))) d.setDate(d.getDate() - 1); // a streak can be "alive" until the day is over
  while (days.has(localDate(d))) {
    n++;
    d.setDate(d.getDate() - 1);
  }
  return n;
}

/** Summary numbers for the teacher view. */
export function summary(p: StudentProgress) {
  const all = Object.values(p.facts);
  return {
    tried: all.length,
    revisit: all.filter((f) => f.s === 1).length,
    correct: all.filter((f) => f.s === 2).length,
    fluent: all.filter((f) => f.s === 3).length,
    unitsDone: UNITS.filter((u) => (p.units[u.id]?.stage ?? 0) >= stagesFor(u).length).length,
  };
}

/**
 * A times-tables unit the placement diagnostic showed the student is solid on (Meliesa, Oct 29: a student solid on
 * the facts skips those units and does the rest of each lesson). Multiplication units on a solid facts sample;
 * division units only when the division sample was solid too.
 */
export const unitSkipped = (p: StudentProgress, unit: string): boolean =>
  !!p.diagnostic?.solid && (unit.startsWith("t") || (unit.startsWith("d") && !!p.diagnostic.divSolid));
